/** Resolve Brand Library URLs for HTML document layouts (editor + download). */
export function lookupAssetUrl(assetUrls, assetId) {
  if (assetId == null || assetId === '') return null;
  const n = Number(assetId);
  if (Number.isFinite(n)) {
    return assetUrls?.[n] || assetUrls?.[String(n)] || null;
  }
  return assetUrls?.[assetId] || null;
}

/**
 * Normalize a picked asset (candidate row, library list item or library detail) into an asset_urls entry.
 * @returns {object | null} null when the asset carries no usable URL
 */
export function assetUrlEntry(asset) {
  if (!asset) return null;
  const rend = asset.renditions || {};
  const transparentUrl = asset.transparent_url || rend.transparent?.url;
  const entry = {
    web_url: asset.web_url || rend.web?.url,
    thumb_url: asset.thumb_url || rend.thumb?.url,
    original_url: asset.original_url || rend.original?.url,
    transparent_url: transparentUrl,
    logo_display_url: asset.logo_display_url || transparentUrl,
    mime_type: asset.mime_type,
    is_vector: asset.is_vector,
    has_alpha: asset.has_alpha,
    svg_background_removed: asset.svg_background_removed,
    width_px: asset.width_px,
    height_px: asset.height_px,
    focal_point: asset.focal_point,
  };
  const hasUrl = entry.web_url || entry.thumb_url || entry.original_url || entry.transparent_url
    || entry.logo_display_url;
  return hasUrl ? entry : null;
}

export function resolveLogoUrl(logoId, assetUrls) {
  const info = lookupAssetUrl(assetUrls, logoId);
  if (!info) return null;
  if (info.logo_display_url) return info.logo_display_url;
  const mime = (info.mime_type || '').toLowerCase();
  if (info.is_vector || mime === 'image/svg+xml' || mime === 'image/png') {
    return info.original_url || info.web_url || null;
  }
  return info.original_url || info.web_url || null;
}

function isVectorInfo(info) {
  return Boolean(info?.is_vector) || (info?.mime_type || '').toLowerCase() === 'image/svg+xml';
}

/**
 * True when the asset can show what is behind it (SVG, or raster with real alpha).
 * has_alpha null means "not checked yet": PNG/WebP/GIF are then assumed transparent.
 */
export function isTransparentAsset(info) {
  if (!info) return false;
  if (isVectorInfo(info)) return true;
  if (info.has_alpha === true) return true;
  if (info.has_alpha === false) return false;
  const mime = (info.mime_type || '').toLowerCase();
  return mime === 'image/png' || mime === 'image/webp' || mime === 'image/gif';
}

export function resolvePhotoUrl(assetId, assetUrls) {
  const info = lookupAssetUrl(assetUrls, assetId);
  if (isVectorInfo(info)) {
    return info.transparent_url || info.original_url || info.web_url || info.thumb_url || null;
  }
  return info?.web_url || info?.thumb_url || null;
}

export const CUTOUT_ATTR = 'data-mc-cutout';

/** Rendition the export pipeline should fetch for a bound img. */
export function renditionForAsset(info, { logo = false } = {}) {
  if (isVectorInfo(info)) return 'transparent';
  return logo ? 'original' : 'web';
}

/**
 * Mark a photo-slot img (and its frame) as a cutout so CSS drops the frame fill/overlay and uses contain.
 * @returns {boolean} whether the slot is a cutout
 */
export function applyCutoutState(img, info) {
  const cutout = isTransparentAsset(info);
  const frame = img.closest('figure, .mc-photo');
  [img, frame].forEach((el) => {
    if (!el) return;
    if (cutout) el.setAttribute(CUTOUT_ATTR, '1');
    else el.removeAttribute(CUTOUT_ATTR);
  });
  return cutout;
}

export function resolvePhotoThumbUrl(assetId, assetUrls) {
  const info = lookupAssetUrl(assetUrls, assetId);
  return info?.thumb_url || null;
}

/** If web rendition fails (404), try thumb once. Preview only — export inlines via API. */
export function attachPhotoLoadFallback(img, assetId, assetUrls) {
  if (img.dataset.mcThumbFallback === '1') return;
  const web = resolvePhotoUrl(assetId, assetUrls);
  const thumb = resolvePhotoThumbUrl(assetId, assetUrls);
  if (!thumb || thumb === web) return;
  img.dataset.mcThumbFallback = '1';
  const handler = () => {
    img.removeEventListener('error', handler);
    if (img.src === web) {
      img.src = thumb;
    }
  };
  img.addEventListener('error', handler);
}
